import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { putCookie, useAnalyticsPageView, useEffectOnce } from '@chilecompra/react-kit'
import {
  Button,
  Card,
  Input,
  Radio,
  Select,
  Toast,
  Typography,
  useInput,
  useRadio,
  useSelect,
  useViewport
} from '@chilecompra/react-kit/components'
import { BUSINESS_SIZES_OPTIONS, BUSINESS_TYPES_OPTIONS } from '../../config/settings/constants'
import {
  onCleanGuestBusinessThunk,
  onCreateTemporarySellerThunk,
  onGetAffidavitThunk,
  onGetGuestBusinessOptionsThunk,
  onGuestBusinessDistrictsThunk
} from './RegisterWizard.actions'
import {
  Root,
  DesktopNavigation,
  Divided,
  MobileNavigation,
  Section,
  Strong,
  SubTitle,
  Title,
  Instructions,
  ExternalLink
} from './RegisterWizardBusinessInformation.styles'
import { REACT_APP_MERCADO_PUBLICO_PROVEEDOR_URL } from '../../config/settings/environment'
import routes from '../../config/settings/routes'

/**
 * The RegisterWizardBusinessInformation's container.
 */
const RegisterWizardBusinessInformation = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { business, loading } = useSelector(state => state.registerWizard)
  const { size } = useViewport()

  const { value: nicknameValue, onChange: handleNicknameChange } = useInput({
    initialValue: business?.entityType === 'natural' ? business?.name : '',
    overwriteCallback: () => (business?.entityType === 'natural' ? business?.name : '')
  })
  const { value: typeValue, onChange: handleTypeChange } = useSelect({
    initialValue: business?.type,
    overwriteCallback: () => business?.type
  })
  const { value: sizeValue, onChange: handleSizeChange } = useSelect({
    initialValue: business?.size,
    overwriteCallback: () => business?.size
  })
  const { value: incomeBracketValue, onChange: handleIncomeBracketChange } = useSelect({
    initialValue: business?.incomeBracket,
    overwriteCallback: () => business?.incomeBracket
  })
  const { value: addressValue, onChange: handleAddressChange } = useInput({
    initialValue: business?.address,
    overwriteCallback: () => business?.address
  })
  const {
    value: districtValue,
    onChange: handleDistrictChange,
    onReset: handleDistrictReset
  } = useSelect({
    initialValue: business?.district,
    overwriteCallback: () => business?.district
  })
  const { value: stateValue, onChange: handleStateChange } = useSelect({
    initialValue: business?.state,
    changeCallback: updatedValue => {
      dispatch(onGuestBusinessDistrictsThunk({ stateID: updatedValue }))
      handleDistrictReset()
    },
    overwriteCallback: () => business?.state
  })
  const [disableRadioButton, setDisableRadioButton] = useState(false)
  const [overwriteRadioValue, setOverwriteRadioValue] = useState('')
  const [strTempSigned, setStrTempSigned] = useState(false)

  const { value: conformationValue, onChange: handleConformationChange } = useRadio({
    initialValue: '',
    overwriteCallback: () => overwriteRadioValue
  })
  const handlePrev = () =>
    dispatch(
      onCleanGuestBusinessThunk({
        callback: () => onChangeStep('business-search')
      })
    )

  const handleNext = useCallback(
    () =>
      dispatch(
        onCreateTemporarySellerThunk({
          address: addressValue,
          conformation: conformationValue,
          district: districtValue,
          incomeBracket: incomeBracketValue,
          nickname: nicknameValue,
          size: sizeValue,
          state: stateValue,
          callback: () => onChangeStep('business-areas')
        })
      ),
    [
      dispatch,
      onCreateTemporarySellerThunk,
      onChangeStep,
      addressValue,
      conformationValue,
      districtValue,
      incomeBracketValue,
      nicknameValue,
      sizeValue,
      stateValue
    ]
  )

  const handleValidate = () => {
    dispatch(onGetAffidavitThunk({ onSuccessCallback: handleNext }))
  }

  const handleGoToAffidavit = () => {
    putCookie('dniRegister', business.dni)
    window.open(`${REACT_APP_MERCADO_PUBLICO_PROVEEDOR_URL}${routes.affidavitRegister}`, '_blank')
  }

  const disabledNext = useMemo(() => {
    if (business?.entityType === 'natural') {
      return (
        addressValue === '' ||
        districtValue === '' ||
        incomeBracketValue === '' ||
        nicknameValue === '' ||
        stateValue === ''
      )
    }

    return (
      addressValue === '' ||
      conformationValue === '' ||
      districtValue === '' ||
      nicknameValue === '' ||
      sizeValue === '' ||
      stateValue === '' ||
      loading ||
      !strTempSigned
    )
  }, [
    addressValue,
    conformationValue,
    districtValue,
    incomeBracketValue,
    loading,
    nicknameValue,
    sizeValue,
    stateValue,
    strTempSigned
  ])
  useEffect(() => {
    if (business?.isPublicOrg) {
      setDisableRadioButton(true)
      setOverwriteRadioValue('2')
    }
  }, [business?.isPublicOrg])
  useEffectOnce(() => {
    dispatch(onGetGuestBusinessOptionsThunk())
  })

  useEffect(() => {
    if (business?.dni) {
      return
    }

    handlePrev()
  }, [business?.dni])

  useEffect(() => {
    const handleAffidavitTempSigned = () => setStrTempSigned(Boolean(localStorage.getItem('affidavitTempSigned')))

    window.addEventListener('storage', handleAffidavitTempSigned)

    return () => window.removeEventListener('storage', handleAffidavitTempSigned)
  }, [])

  useAnalyticsPageView('/paso/3/informacion_empresa')
  return (
    <Root>
      <Section item>
        <Title>Revisa tus datos</Title>
      </Section>
      <Section item margin="23px 0 33px" maxWidth="683px">
        <Instructions>
          Esta es la información asociada al RUT <Strong>{business?.dni}</Strong> según el registro del Servicio de
          Impuestos Internos. Si deseas actualizarla debes hacerlo directamente en SII.cl
        </Instructions>
      </Section>
      <Section container margin="3px 0 0" maxWidth="693px">
        <Section item md={6} padding={!size.isDownToMedium && '0 21px 0 0'} xs={12}>
          <Input disabled loading={business?.loading} label="razón social" value={business?.name || ''} />
        </Section>
        <Section item md={6} padding={!size.isDownToMedium && '0 0 0 21px'} xs={12}>
          <Input
            label={business?.entityType === 'legal' ? 'nombre de fantasía' : 'nombre comercial'}
            loading={business?.loading}
            value={nicknameValue}
            onChange={handleNicknameChange}
          />
        </Section>
      </Section>
      <Section container margin="3px 0 0" maxWidth="693px">
        <Section item md={6} padding={!size.isDownToMedium && '0 21px 0 0'} xs={12}>
          <Select
            disabled={Boolean(business?.type)}
            label="tipo de empresa"
            loading={business?.loading}
            options={BUSINESS_TYPES_OPTIONS}
            value={typeValue}
            onChange={handleTypeChange}
          />
        </Section>
        <Section item md={6} padding={!size.isDownToMedium && '0 0 0 21px'} xs={12}>
          {business?.entityType === 'legal' && (
            <Select
              disabled={Boolean(business?.size)}
              info={
                <>
                  UF (Unidad de Fomento) es una unidad de cuenta usada en Chile, reajustable de acuerdo con la
                  inflación.&nbsp;
                  <ExternalLink
                    href="https://www.sii.cl/valores_y_fechas/index_valores_y_fechas.html"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Revisa su valor aquí
                  </ExternalLink>
                  .
                </>
              }
              label="tamaño de empresa - facturación anual"
              loading={business?.loading}
              options={BUSINESS_SIZES_OPTIONS}
              value={sizeValue}
              onChange={handleSizeChange}
            />
          )}
          {business?.entityType === 'natural' && (
            <Select
              disabled={Boolean(business?.incomeBracket)}
              info="UTA: Unidad Tributaria Anual"
              label="tramo de renta anual"
              loading={business?.loading}
              options={business?.incomeBrackets || []}
              value={incomeBracketValue}
              onChange={handleIncomeBracketChange}
            />
          )}
        </Section>
      </Section>
      <Section container margin={business?.entityType === 'legal' ? '8px 0 0' : '3px 0 0'} maxWidth="693px">
        <Input
          disabled={Boolean(business?.address)}
          label="dirección"
          loading={business?.loading}
          value={addressValue}
          onChange={handleAddressChange}
        />
      </Section>
      <Section container margin="3px 0 0" maxWidth="693px">
        <Section item md={6} padding={!size.isDownToMedium && '0 21px 0 0'} xs={12}>
          <Select
            disabled={Boolean(business?.state)}
            label="región"
            loading={business?.loading}
            options={business?.states || []}
            value={stateValue}
            onChange={handleStateChange}
          />
        </Section>
        <Section item md={6} padding={!size.isDownToMedium && '0 0 0 21px'} xs={12}>
          <Select
            disabled={Boolean(business?.district) || stateValue === ''}
            label="comuna"
            loading={business?.loading || business?.districtLoading}
            options={business?.districts || []}
            value={districtValue}
            onChange={handleDistrictChange}
          />
        </Section>
      </Section>
      {business?.entityType === 'legal' && (
        <Section container margin="3px 0 0" maxWidth="693px">
          <Radio
            direction={size.isTiny ? 'vertical' : 'horizontal'}
            label="tipo de conformación de la empresa"
            disabled={disableRadioButton}
            options={[
              {
                label: 'Empresa en un día',
                value: '1'
              },
              {
                label: 'Escritura pública',
                value: '2'
              }
            ]}
            value={conformationValue}
            onChange={handleConformationChange}
          />
        </Section>
      )}

      {business?.type === 22 && (
        <>
          <Divided margin="58px 0 38px" width="90%" />
          <Section item margin="23px 0 33px" maxWidth="683px">
            <Instructions> Completa y firma la Declaración Jurada de Socios y Accionistas </Instructions>
            <Typography variant="body2" color="black1" margin="15px 0 0">
              Documento que deben firmar las empresas jurídicas que se inscriban en Mercado Público, con el fin de
              informar sobre socios, accionistas y administradores que son parte de esta, y así aumentar los niveles de
              transparencia y probidad en las compras públicas.
            </Typography>
          </Section>
          <Section item margin="23px 0 33px" maxWidth="683px">
            <Card color="blank" padding="24px" margin="16px 0" enableBorder>
              <Section container justifyContent={size.isTiny ? 'center' : 'flex-start'}>
                <Section item margin="23px 0 33px" maxWidth="683px">
                  <SubTitle width={size.isTiny ? '100%' : '80%'}>
                    Declaración Jurada de Socios y Accionistas {business?.name || ''}
                  </SubTitle>
                </Section>
              </Section>
              <Section container>
                <Section item margin="23px 0 33px" maxWidth="683px">
                  <Typography variant="body2" color="black1" margin="15px 0 0">
                    Deberá ingresar la información de los socios y accionistas y porcentaje de participación. Sí la
                    persona jurídica no está compuesta por socios o accionistas, deberá ingresar su Directorio o
                    Representante/s Legal/es dividiendo el 100% en el número de personas a ingresar.
                  </Typography>
                </Section>
              </Section>
              <Section container justifyContent={size.isTiny ? 'center' : 'space-between'}>
                <Section item xs={12} md={6} lg={5} margin="0 26px">
                  <DesktopNavigation>
                    <Button
                      color="primary"
                      variant="outlined"
                      margin="23px 0 0"
                      onClick={handleGoToAffidavit}
                      width="283px"
                    >
                      Completar Declaración Jurada
                    </Button>
                  </DesktopNavigation>
                </Section>
                <Section item xs={12} md={6} lg={6}>
                  <Typography variant="subtitle" color="black3" margin="21px 0 0">
                    Se abrirá una nueva pestaña para completar la Declaración Jurada. Una vez lista, presiona
                    "Siguiente" para validar que el documento esté firmado.
                  </Typography>
                </Section>
              </Section>
            </Card>
          </Section>

          {business?.entityType === 'legal' && (!strTempSigned || nicknameValue === '') && (
            <Section container margin="3px 0 0" maxWidth="693px">
              <Toast title="Importante:" severity="warning">
                <br />
                Para continuar con el siguiente paso del registro, deberás completar el campo{' '}
                <Strong>"Nombre de fantasía"</Strong> y la{' '}
                <Strong>"Declaración Jurada de Socios y Accionistas".</Strong>
              </Toast>
            </Section>
          )}
        </>
      )}
      {!size.isTiny && (
        <Section container margin="52px 0 0 0">
          <DesktopNavigation>
            <Button type="button" variant="text" width="265px" onClick={handlePrev}>
              Atrás
            </Button>
            <Button
              color="primary"
              disabled={disabledNext}
              loading={loading}
              margin="0 0 0 30px"
              type="button"
              width="265px"
              onClick={handleValidate}
            >
              Siguiente
            </Button>
          </DesktopNavigation>
        </Section>
      )}
      {size.isTiny && (
        <MobileNavigation>
          <Button type="button" variant="text" width="168px" onClick={handlePrev}>
            Atrás
          </Button>
          <Button
            color="primary"
            disabled={disabledNext}
            loading={loading}
            type="button"
            width="168px"
            onClick={handleNext}
          >
            Siguiente
          </Button>
        </MobileNavigation>
      )}
    </Root>
  )
}

RegisterWizardBusinessInformation.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardBusinessInformation.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardBusinessInformation
